<template>
  <div class="d-flex fill-height app-bg overflow-hidden position-relative font-sans" style="width: 100%; height: 100vh">
    <v-navigation-drawer
      v-model="drawer"
      absolute
      temporary
      color="#000000"
      dark
      width="280"
      v-if="$vuetify.breakpoint.smAndDown"
      style="z-index: 100">
      <div class="px-6 mb-8 d-flex align-center">
        <v-btn depressed height="48" width="48" class="mr-1 rounded-lg" color="rgba(255, 255, 255, 0.1)" @click="$router.push('/dashboard')">
          <v-icon color="white" size="24">mdi-arrow-left</v-icon>
        </v-btn>
        <div class="pa-6 font-weight-black white--text text-h5 tracking-tight">Mail<span class="primary--text">.</span></div>
      </div>
      <v-treeview
        :items="folders"
        activatable
        item-key="path"
        dense
        dark
        class="folder-tree pa-0"
        :active.sync="activeFolders"
        @update:active="onFolderSelect">
        <template v-slot:prepend="{ item, open }">
          <v-icon small class="mr-1" :color="selectedFolder === item.path ? 'white' : 'grey'">
            {{ getFolderIcon(item.name) }}
          </v-icon>
        </template>

        <template v-slot:label="{ item }">
          <div @dragover.prevent @drop="onDrop($event, item.path)" class="d-flex align-center fill-height" style="width: 100%; cursor: pointer">
            <span class="body-2 font-weight-medium" :class="selectedFolder === item.path ? 'white--text' : 'grey--text'">
              {{ item.name }}
            </span>
          </div>
        </template>
      </v-treeview>
    </v-navigation-drawer>

    <div v-if="$vuetify.breakpoint.mdAndUp" class="d-flex flex-column sidebar-panel pt-6">
      <div class="px-6 mb-8 d-flex align-center">
        <v-btn depressed height="48" width="48" class="mr-3 rounded-lg" color="rgba(255, 255, 255, 0.1)" @click="$router.push('/dashboard')">
          <v-icon color="white" size="24">mdi-arrow-left</v-icon>
        </v-btn>

        <div class="pa-6 font-weight-black white--text text-h5 tracking-tight">Mail<span class="primary--text">.</span></div>
      </div>

      <div class="px-4 mb-6">
        <!-- Sync Removed -->
      </div>

      <div class="px-3 flex-grow-1">
        <div class="d-flex align-center justify-space-between px-3 mb-2">
          <div class="overline grey--text text--darken-2 font-weight-bold">FOLDERS</div>
          <div>
            <v-btn icon x-small color="grey darken-1" @click="refreshAll" :loading="refreshing">
              <v-icon>mdi-refresh</v-icon>
            </v-btn>
            <v-btn icon x-small color="grey darken-1" @click="showCreateFolder = true">
              <v-icon>mdi-plus</v-icon>
            </v-btn>
          </div>
        </div>

        <v-treeview
          :items="folders"
          activatable
          item-key="path"
          dense
          dark
          class="folder-tree pa-0"
          :active.sync="activeFolders"
          @update:active="onFolderSelect">
          <template v-slot:prepend="{ item, open }">
            <v-icon small class="mr-1" :color="selectedFolder === item.path ? 'white' : 'grey'">
              {{ getFolderIcon(item.name) }}
            </v-icon>
          </template>

          <template v-slot:label="{ item }">
            <div @dragover.prevent @drop="onDrop($event, item.path)" class="d-flex align-center fill-height" style="width: 100%; cursor: pointer">
              <span class="body-2 font-weight-medium" :class="selectedFolder === item.path ? 'white--text' : 'grey--text'">
                {{ item.name }}
              </span>
            </div>
          </template>
        </v-treeview>
      </div>

      <div class="pa-4 mt-auto">
        <div class="d-flex align-center pa-3 rounded-xl glass-panel-dark">
          <v-avatar size="32" color="grey darken-3"><v-icon small>mdi-account</v-icon></v-avatar>

          <div class="ml-3 caption grey--text font-weight-bold">{{ userService.getFullName() }}</div>
        </div>
      </div>
    </div>

    <div class="d-flex flex-column list-panel border-r-subtle" :class="{ 'd-none': $vuetify.breakpoint.smAndDown && showMobileDetail }">
      <div class="glass-header px-5 py-4 d-flex align-center flex-shrink-0 z-index-10">
        <v-btn icon small dark class="mr-3 hidden-md-and-up" @click="drawer = !drawer">
          <v-icon>mdi-menu</v-icon>
        </v-btn>

        <div class="font-weight-bold white--text text-h6 tracking-tight">Posteingang</div>
        <v-spacer></v-spacer>

        <v-btn
          v-if="selectedFolder === 'INBOX' && totalMails > limit"
          icon
          small
          color="grey darken-1"
          class="mr-1"
          @click="loadEverythingAtOnce"
          :loading="loadingMails">
          <v-icon>mdi-skip-backward</v-icon>
        </v-btn>

        <v-btn icon small color="grey darken-1"><v-icon>mdi-filter-variant</v-icon></v-btn>
      </div>

      <div class="px-4 pb-2 pt-2 flex-shrink-0">
        <v-text-field
          v-model="search"
          @input="onSearchInput"
          placeholder="Suche (Betreff, Absender)..."
          prepend-inner-icon="mdi-magnify"
          filled
          rounded
          dense
          hide-details
          dark
          color="primary"
          background-color="#1A1A1A"
          class="modern-search-field">
        </v-text-field>
      </div>

      <div class="flex-grow-1 overflow-y-auto custom-scrollbar px-3 pb-2 pt-2" @scroll="onScroll" ref="mailListContainer">
        <div v-if="loadingMails && page === 1" class="mt-2 px-1">
          <v-skeleton-loader dark type="list-item-avatar-two-line" v-for="n in 6" :key="n" class="mb-3 transparent"></v-skeleton-loader>
        </div>

        <div
          v-else-if="mails.length === 0 && !loadingMails"
          class="d-flex flex-column align-center justify-center fill-height grey--text text--darken-2">
          <v-icon color="grey darken-3" size="64" class="mb-4">mdi-inbox-outline</v-icon>
          <div class="font-weight-medium">
            {{ search ? "Nichts gefunden" : "Alles erledigt" }}
          </div>
        </div>

        <div v-else class="d-flex flex-column">
          <div
            v-for="mail in mails"
            :key="mail._id"
            @click="selectMail(mail)"
            @contextmenu.prevent="showContext($event, mail)"
            draggable="true"
            @dragstart="onDragStart($event, mail)"
            class="mail-card rounded-xl mb-2 pa-4 pointer position-relative overflow-hidden"
            :class="{
              'active-mail-card': activeMail && activeMail._id === mail._id,

              'unread-mail': !mail.isRead,

              'is-processing': mail.category === 'Processing...',
            }">
            <div class="d-flex align-start justify-space-between mb-1">
              <div class="d-flex align-center" style="max-width: 80%">
                <v-avatar size="24" :color="getCategoryColor(mail.category)" class="mr-2 elevation-2">
                  <span class="white--text caption font-weight-bold" style="font-size: 10px !important">
                    {{ (mail.from.name || mail.from.address)[0].toUpperCase() }}
                  </span>
                </v-avatar>

                <span class="font-weight-bold text-truncate white--text body-2">
                  {{ mail.from.name || mail.from.address }}
                </span>
              </div>

              <span class="caption grey--text text--darken-1 font-weight-medium">{{ formatDate(mail.date) }}</span>
            </div>

            <div class="body-2 grey--text text--lighten-1 text-truncate mb-1 pl-1" :class="{ 'white--text font-weight-bold': !mail.isRead }">
              {{ mail.subject || "(Kein Betreff)" }}
            </div>

            <div class="d-flex align-center pl-1 mb-2">
              <v-chip v-if="mail.assignedTo" x-small color="blue darken-3" label class="mr-1" style="height: 16px; font-size: 10px">
                {{ mail.assignedTo.vorname ? mail.assignedTo.vorname[0].toUpperCase() : "U" }}
              </v-chip>
              <v-chip
                v-if="mail.category && mail.category !== 'Inbox' && mail.category !== 'Processing...'"
                x-small
                color="grey darken-3"
                outlined
                label
                style="height: 16px; font-size: 10px; border-color: rgba(255, 255, 255, 0.2)">
                {{ mail.category.toUpperCase() }}
              </v-chip>
            </div>

            <div class="d-flex align-center pl-1">
              <template v-if="mail.category === 'Processing...'">
                <div class="d-flex align-center">
                  <v-progress-circular indeterminate size="12" width="2" color="purple accent-2" class="mr-2"></v-progress-circular>

                  <span class="caption purple--text text--accent-2 font-weight-bold fade-in-out">AI analysiert...</span>
                </div>
              </template>

              <template v-else>
                <span class="caption grey--text text--darken-1 text-truncate" style="opacity: 0.8">
                  {{ mail.aiAnalysis ? mail.aiAnalysis.summary : "Keine Vorschau" }}
                </span>
              </template>
            </div>

            <div class="card-glow"></div>
          </div>
        </div>
        <div v-if="loadingMore" class="py-4 text-center">
          <v-progress-circular indeterminate size="24" width="2" color="primary"></v-progress-circular>
        </div>
      </div>
    </div>

    <div
      class="d-flex flex-column detail-panel black"
      :class="{
        'mobile-fullscreen': $vuetify.breakpoint.smAndDown && showMobileDetail,

        'd-none': $vuetify.breakpoint.smAndDown && !showMobileDetail,

        'flex-grow-1': $vuetify.breakpoint.mdAndUp,
      }">
      <div v-if="!activeMail" class="d-flex align-center justify-center fill-height flex-column bg-pattern">
        <div class="d-flex align-center justify-center rounded-circle glass-panel mb-6 elevation-10" style="width: 120px; height: 120px">
          <v-icon size="56" color="grey darken-1">mdi-email-outline</v-icon>
        </div>

        <div class="grey--text text--darken-1 font-weight-medium">Wähle eine Nachricht aus</div>
      </div>

      <template v-else>
        <div class="glass-header px-6 py-3 d-flex align-center flex-shrink-0 z-index-10 border-b-subtle">
          <v-btn icon small @click="showMobileDetail = false" class="mr-2 hidden-md-and-up white--text">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>

          <v-spacer></v-spacer>

          <div class="d-flex align-center glass-pill pa-1">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="archiveMail(activeMail)" color="grey lighten-1"
                  ><v-icon size="20">mdi-archive-outline</v-icon></v-btn
                >
              </template>

              <span>Archivieren</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="deleteMail(activeMail)" color="grey lighten-1" class="mx-1"
                  ><v-icon size="20">mdi-delete-outline</v-icon></v-btn
                >
              </template>

              <span>Löschen</span>
            </v-tooltip>

            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="openForward" color="grey lighten-1"
                  ><v-icon size="20">mdi-share-variant</v-icon></v-btn
                >
              </template>

              <span>Weiterleiten</span>
            </v-tooltip>
          </div>

          <div class="mx-3" style="height: 24px; width: 1px; background: rgba(255, 255, 255, 0.1)"></div>

          <v-btn
            text
            small
            color="purple accent-2"
            @click="showMagicDialog = true"
            class="rounded-lg px-2"
            style="background: rgba(224, 64, 251, 0.1)">
            <v-icon left small>mdi-auto-fix</v-icon> <span class="hidden-xs-only font-weight-bold">AI Ops</span>
          </v-btn>

          <v-btn
            v-if="activeMail.customerData && activeMail.customerData.mapped"
            color="success"
            small
            text
            class="ml-2 rounded-lg px-2"
            style="background: rgba(76, 175, 80, 0.1)"
            @click="showCustomerInfo = true">
            <v-icon left small>mdi-card-account-details</v-icon> <span class="hidden-xs-only font-weight-bold">Member</span>
          </v-btn>

          <v-btn v-if="activeMail.customerData && activeMail.customerData.mapped" icon small color="grey" class="ml-1" @click="openLinkDialog">
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>

          <v-chip v-else x-small outlined color="grey darken-1" class="ml-2 pointer hover-scale" @click="openLinkDialog">
            <v-icon left x-small>mdi-link-plus</v-icon> Verknüpfen
          </v-chip>

          <!-- Assignment -->
          <v-chip v-if="activeMail.assignedTo" color="blue darken-3" small label class="ml-2" close @click:close="assignUser('unassign')">
            <v-avatar left size="20" color="blue darken-4">
              <span class="white--text caption font-weight-bold">{{
                activeMail.assignedTo.vorname ? activeMail.assignedTo.vorname[0].toUpperCase() : "U"
              }}</span>
            </v-avatar>
            <span class="font-weight-bold">{{ activeMail.assignedTo.vorname }}</span>
          </v-chip>

          <v-tooltip bottom v-else>
            <template v-slot:activator="{ on, attrs }">
              <v-btn icon small v-bind="attrs" v-on="on" class="ml-1" @click="assignUser('assign')">
                <v-icon color="grey lighten-1">mdi-account-arrow-left-outline</v-icon>
              </v-btn>
            </template>
            <span>Bearbeitung übernehmen</span>
          </v-tooltip>

          <!-- Category -->
          <v-chip
            v-if="activeMail.category && activeMail.category !== 'Inbox' && activeMail.category !== 'Processing...'"
            x-small
            color="grey darken-3"
            class="ml-2 font-weight-bold letter-spacing-1"
            outlined
            label
            style="border-color: rgba(255, 255, 255, 0.2)">
            {{ activeMail.category.toUpperCase() }}
          </v-chip>
        </div>

        <div class="flex-grow-1 overflow-y-auto custom-scrollbar relative-wrapper bg-gradient-subtle">
          <div class="pa-6 pb-2">
            <h1 class="white--text text-h5 font-weight-bold mb-6 tracking-tight">{{ activeMail.subject }}</h1>

            <div class="d-flex align-center justify-space-between mb-6">
              <div class="d-flex align-center">
                <v-avatar color="grey darken-3" size="48" class="mr-4 elevation-4">
                  <span class="white--text font-weight-bold text-h6">{{ (activeMail.from.name || activeMail.from.address)[0].toUpperCase() }}</span>
                </v-avatar>

                <div>
                  <div class="white--text font-weight-bold subtitle-1">{{ activeMail.from.name }}</div>

                  <div class="grey--text text--lighten-1 caption">{{ activeMail.from.address }}</div>

                  <div class="grey--text text--darken-1 caption mt-1">An: {{ activeMail.to.map((t) => t.name || t.address).join(", ") }}</div>
                </div>
              </div>

              <div class="text-right">
                <div class="grey--text text--lighten-1 caption font-weight-bold">
                  {{
                    new Date(activeMail.date).toLocaleDateString([], {
                      weekday: "short",
                      day: "2-digit",
                      month: "short",
                    })
                  }}
                </div>

                <div class="grey--text text--darken-1 caption">
                  {{ new Date(activeMail.date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }}
                </div>
              </div>
            </div>

            <div v-if="activeMail.attachments && activeMail.attachments.length > 0" class="mb-4">
              <div class="caption grey--text mb-2">ANHÄNGE ({{ activeMail.attachments.length }})</div>
              <div class="d-flex flex-wrap">
                <v-chip
                  v-for="(att, i) in activeMail.attachments"
                  :key="i"
                  class="mr-2 mb-2"
                  color="grey darken-3"
                  text-color="white"
                  @click="downloadAttachment(att)">
                  <v-icon left small>mdi-paperclip</v-icon>
                  {{ att.filename }} <span class="grey--text ml-1 text--lighten-1">({{ Math.round(att.size / 1024) }} KB)</span>
                  <v-icon right small>mdi-download</v-icon>
                </v-chip>
              </div>
            </div>

            <div v-if="activeMail.aiAnalysis" class="pa-4 rounded-xl mb-4 ai-analysis-box">
              <div class="d-flex align-center mb-2">
                <v-icon small color="purple accent-2" class="mr-2">mdi-shimmer</v-icon>

                <span class="caption purple--text text--accent-2 font-weight-bold tracking-wide">ROODY INSIGHTS</span>
              </div>

              <div class="grey--text text--lighten-2 body-2" style="line-height: 1.6">{{ activeMail.aiAnalysis.summary }}</div>
            </div>
          </div>

          <v-divider class="grey darken-4 mx-6"></v-divider>

          <div class="pa-6">
            <div class="mail-renderer pa-6 rounded-lg elevation-2 strip-images">
              <div v-html="activeMail.body"></div>
            </div>
          </div>

          <div class="pa-6 pt-2">
            <!-- Loading Body Indicator -->
            <div v-if="activeMail.loadingBody" class="d-flex align-center justify-center py-8">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
              <span class="ml-3 grey--text">Lade Inhalt...</span>
            </div>

            <div v-else-if="isRegenerating || activeMail.category === 'Processing...'" class="text-center py-8">
              <div class="ai-loader mx-auto mb-4"></div>
              <div class="caption grey--text fade-in-out">Roody analysiert...</div>
            </div>

            <div v-else-if="!activeMail.draft && !replyMode && !isForwarding" class="text-center py-4">
              <v-btn
                v-if="isInternalUnmapped"
                large
                depressed
                color="grey darken-3"
                class="rounded-xl white--text border-glow"
                @click="openLinkDialog">
                <v-icon left color="success">mdi-link-plus</v-icon> Jetzt verknüpfen
              </v-btn>

              <v-btn v-else large depressed class="gradient-btn rounded-xl white--text" @click="createDraft">
                <v-icon left>mdi-robot</v-icon> AI Draft erstellen
              </v-btn>
            </div>

            <div
              v-if="
                activeMail.category &&
                (activeMail.category.toLowerCase().includes('künd') ||
                  activeMail.category.toLowerCase().includes('cancell') ||
                  activeMail.category.toLowerCase().includes('termination')) &&
                activeMail.customerData &&
                activeMail.customerData.mapped
              "
              class="mt-4">
              <mail-termination-wizard :user="activeMail.customerData" @cancel="forceDraftView = true" @completed="handleTerminationAndOpenNotes" />

              <div class="text-center mt-2">
                <v-btn text x-small color="grey" @click="forceDraftView = true"> Normale AI Antwort anzeigen </v-btn>
              </div>
            </div>

            <div v-else-if="(activeMail.draft || forceDraftView) && !replyMode && !isForwarding" class="mt-4">
              <div class="ai-draft-card rounded-xl overflow-hidden elevation-6">
                <div class="d-flex align-center px-5 py-3 draft-header">
                  <v-icon small color="white" class="mr-2">mdi-robot-outline</v-icon>
                  <span class="subtitle-2 white--text font-weight-bold">Vorschlag</span>
                  <v-spacer></v-spacer>
                  <v-btn x-small text color="white" style="opacity: 0.7" @click="showMagicDialog = true">
                    <v-icon left x-small>mdi-refresh</v-icon> Neu generieren
                  </v-btn>
                </div>

                <div
                  class="pa-5 grey--text text--lighten-1 body-1 font-mono"
                  style="white-space: pre-wrap; line-height: 1.6"
                  v-html="activeMail.draft"></div>

                <div class="pa-3 grey darken-4 d-flex justify-end border-t-subtle">
                  <v-btn color="white" light depressed small class="font-weight-bold rounded-lg px-4" @click="initReply">
                    Verwenden <v-icon right small>mdi-arrow-down</v-icon>
                  </v-btn>
                </div>
              </div>
            </div>
          </div>

          <div style="height: 140px"></div>
        </div>

        <div class="floating-action-bar pa-4">
          <v-expand-transition>
            <div v-if="replyMode" class="d-flex flex-column glass-panel-dark rounded-xl pa-4 elevation-12 border-glow" style="max-height: 60vh">
              <div v-if="isForwarding" class="mb-3 px-1 d-flex align-center border-b-subtle pb-2 flex-shrink-0">
                <span class="caption grey--text mr-3">An:</span>
                <input v-model="forwardTo" placeholder="Empfänger E-Mail..." class="modern-input flex-grow-1 font-weight-bold" />
              </div>

              <div class="flex-grow-1 overflow-y-auto custom-scrollbar pr-2" style="min-height: 150px">
                <v-textarea
                  v-model="replyText"
                  outlined
                  dense
                  hide-details
                  dark
                  color="primary"
                  background-color="transparent"
                  auto-grow
                  class="no-border-input"
                  placeholder="Schreibe deine Antwort...">
                </v-textarea>
              </div>

              <div class="d-flex justify-end align-center mt-2 pt-2 border-t-subtle flex-shrink-0">
                <v-btn text small color="grey" class="mr-2 white--text rounded-lg" @click="replyMode = false"> Abbrechen </v-btn>

                <v-btn color="primary" depressed class="rounded-lg gradient-btn shadow-md" @click="sendReply" :loading="sending">
                  <v-icon left small>mdi-send</v-icon> Senden
                </v-btn>
              </div>
            </div>

            <div v-else class="d-flex justify-center">
              <div class="glass-pill elevation-10 d-flex pa-1">
                <v-btn large text color="white" class="rounded-pill px-6" @click="initReply" :disabled="activeMail.category === 'Processing...'">
                  <v-icon left color="primary">mdi-reply</v-icon> Antworten
                </v-btn>

                <div class="my-2" style="width: 1px; background: rgba(255, 255, 255, 0.1)"></div>

                <v-btn
                  icon
                  large
                  color="grey lighten-1"
                  class="rounded-circle ml-1"
                  @click="openForward"
                  :disabled="activeMail.category === 'Processing...'">
                  <v-icon>mdi-share-variant</v-icon>
                </v-btn>
              </div>
            </div>
          </v-expand-transition>
        </div>
      </template>
    </div>

    <v-dialog v-model="showCustomerInfo" max-width="900" scrollable :fullscreen="$vuetify.breakpoint.xsOnly">
      <v-card v-if="tacMember" color="#121212" dark class="rounded-xl border-glow">
        <div class="d-flex align-end pa-6 gradient-header">
          <v-avatar size="72" color="white" class="elevation-4 mr-5">
            <span class="primary--text text-h4 font-weight-black">{{ tacMember.initials }}</span>
          </v-avatar>

          <div>
            <div class="text-h4 font-weight-bold white--text mb-1">{{ tacMember.fullName }}</div>

            <v-chip label x-small color="rgba(255,255,255,0.1)" class="font-weight-bold letter-spacing-1">ID: {{ tacMember.memberNumber }}</v-chip>
          </div>

          <v-spacer></v-spacer>

          <v-btn icon large class="mb-2" @click="showCustomerInfo = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>

        <v-card-text class="pa-0">
          <v-tabs v-model="memberTab" background-color="#121212" dark grow height="60" slider-color="primary">
            <v-tab class="font-weight-bold text-capitalize">Übersicht</v-tab>

            <v-tab class="font-weight-bold text-capitalize">Verträge ({{ tacMember.contracts.length }})</v-tab>

            <v-tab class="font-weight-bold text-capitalize">Notizen</v-tab>
          </v-tabs>

          <v-tabs-items v-model="memberTab" class="transparent pa-6" style="min-height: 400px; background: #000">
            <v-tab-item>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card flat color="#161616" class="pa-5 rounded-xl fill-height border-subtle">
                    <div class="overline grey--text mb-4 font-weight-bold">FINANZEN</div>

                    <div class="d-flex align-center justify-space-between mb-4">
                      <span class="grey--text text--lighten-1">Saldo Gesamt</span>

                      <span
                        class="text-h4 font-weight-black"
                        :class="parseFloat(tacMember.balanceTotal) < 0 ? 'red--text text--accent-2' : 'green--text text--accent-3'">
                        CHF {{ tacMember.balanceTotal }}
                      </span>
                    </div>

                    <v-divider class="grey darken-4 mb-4"></v-divider>

                    <div class="d-flex justify-space-between mb-2">
                      <span class="grey--text">Abo-Konto</span>

                      <span class="white--text font-weight-bold">CHF {{ tacMember.balanceMembership }}</span>
                    </div>

                    <div class="d-flex justify-space-between">
                      <span class="grey--text">Konsum-Konto</span>

                      <span class="white--text font-weight-bold">CHF {{ tacMember.balanceConsumption }}</span>
                    </div>
                  </v-card>
                </v-col>

                <v-col cols="12" md="6">
                  <v-card flat color="#161616" class="pa-5 rounded-xl fill-height border-subtle">
                    <div class="overline grey--text mb-4 font-weight-bold">KONTAKT</div>

                    <div class="d-flex align-center mb-3">
                      <v-icon small color="primary" class="mr-3">mdi-email</v-icon>

                      <span class="body-1 white--text">{{ tacMember.email }}</span>
                    </div>

                    <div class="d-flex align-center mb-3">
                      <v-icon small color="primary" class="mr-3">mdi-phone</v-icon>

                      <span class="body-1 white--text">{{ tacMember.phone }}</span>
                    </div>

                    <div class="d-flex align-start">
                      <v-icon small color="primary" class="mr-3 mt-1">mdi-map-marker</v-icon>

                      <span class="body-1 white--text">{{ tacMember.address }}, {{ tacMember.zipCity }}</span>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </v-tab-item>

            <v-tab-item>
              <div v-for="(contract, i) in tacMember.contracts" :key="i" class="mb-4">
                <v-card flat color="#161616" class="rounded-xl border-subtle overflow-hidden">
                  <div class="pa-4 d-flex align-center" :style="`border-left: 4px solid ${contract.color}`">
                    <div>
                      <div class="font-weight-bold white--text subtitle-1">{{ contract.name }}</div>

                      <div class="caption grey--text">{{ contract.start }} - {{ contract.end }}</div>
                    </div>

                    <v-spacer></v-spacer>

                    <v-chip x-small :color="contract.color" label class="font-weight-bold white--text">{{ contract.statusText }}</v-chip>
                  </div>

                  <div v-if="contract.subContracts.length" class="px-4 pb-4">
                    <v-divider class="grey darken-4 mb-3"></v-divider>

                    <div v-for="(sub, k) in contract.subContracts" :key="k" class="d-flex align-center mb-1">
                      <v-icon x-small color="grey" class="mr-2">mdi-plus-circle-outline</v-icon>

                      <span class="caption white--text">{{ sub.name }}</span>
                    </div>
                  </div>
                </v-card>
              </div>
            </v-tab-item>

            <v-tab-item>
              <v-card flat color="#161616" class="pa-0 rounded-xl fill-height border-subtle d-flex flex-column">
                <v-tabs v-model="activeNoteTypeIndex" background-color="transparent" dark slider-color="primary" dense show-arrows>
                  <v-tab v-for="type in noteTypes" :key="type" class="caption font-weight-bold">
                    {{ type }}
                  </v-tab>
                </v-tabs>

                <v-divider class="grey darken-4"></v-divider>

                <div class="pa-4 flex-grow-1">
                  <div class="d-flex align-center justify-space-between mb-2">
                    <span class="caption grey--text">
                      Inhalt für <strong>{{ currentNoteType }}</strong>
                    </span>
                    <span v-if="lastUpdateInfo" class="caption grey--text text--darken-1"> Zuletzt: {{ lastUpdateInfo }} </span>
                  </div>

                  <v-textarea
                    v-model="currentNoteText"
                    outlined
                    dense
                    dark
                    rows="10"
                    background-color="#0a0a0a"
                    :placeholder="`Notizen für ${currentNoteType} hier eingeben...`"
                    hide-details
                    class="font-mono body-2"></v-textarea>

                  <div class="d-flex justify-end mt-4">
                    <v-btn color="primary" depressed class="rounded-lg" :loading="savingComment" @click="saveCurrentNote">
                      <v-icon left small>mdi-content-save</v-icon> {{ currentNoteType }} Speichern
                    </v-btn>
                  </div>
                </div>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-menu v-model="showContextMenu" :position-x="contextMenuX" :position-y="contextMenuY" absolute offset-y dark>
      <v-list dense color="#1E1E1E">
        <v-list-item @click="toggleReadStatus(contextMenuMail)" v-if="contextMenuMail">
          <v-list-item-icon><v-icon small>mdi-email-mark-as-unread</v-icon></v-list-item-icon>
          <v-list-item-title>{{ contextMenuMail.isRead ? "Als ungelesen markieren" : "Als gelesen markieren" }}</v-list-item-title>
        </v-list-item>
        <v-list-item @click="archiveMail(contextMenuMail)" v-if="contextMenuMail">
          <v-list-item-icon><v-icon small>mdi-archive</v-icon></v-list-item-icon>
          <v-list-item-title>Archivieren</v-list-item-title>
        </v-list-item>
        <v-list-item @click="deleteMail(contextMenuMail)" v-if="contextMenuMail">
          <v-list-item-icon><v-icon small>mdi-delete</v-icon></v-list-item-icon>
          <v-list-item-title>Löschen</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <v-dialog v-model="showCreateFolder" max-width="400">
      <v-card color="#121212" dark class="rounded-xl border-glow">
        <div class="pa-5">
          <h3 class="text-h6 font-weight-bold white--text mb-4">Neuer Ordner</h3>
          <v-text-field
            v-model="newFolderName"
            label="Ordnername"
            outlined
            dense
            rounded
            background-color="#1A1A1A"
            autofocus
            @keyup.enter="createFolder"></v-text-field>
          <div class="d-flex justify-end">
            <v-btn text class="grey--text mr-2" @click="showCreateFolder = false">Abbrechen</v-btn>
            <v-btn color="primary" depressed class="rounded-lg" @click="createFolder" :disabled="!newFolderName">Erstellen</v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showLinkDialog" max-width="400">
      <v-card color="#121212" dark class="rounded-xl border-glow">
        <div class="pa-5">
          <div class="d-flex align-center mb-4">
            <v-icon color="success" class="mr-2">mdi-link-variant</v-icon>
            <h3 class="text-h6 font-weight-bold white--text">Mitglied verknüpfen</h3>
          </div>
          <p class="caption grey--text">Gib die E-Mail Adresse des Mitglieds ein.</p>

          <v-text-field
            v-model="linkEmailInput"
            label="E-Mail Adresse"
            outlined
            dense
            rounded
            background-color="#1A1A1A"
            autofocus
            @keyup.enter="linkCustomer"></v-text-field>

          <div class="d-flex justify-end">
            <v-btn text class="grey--text mr-2" @click="showLinkDialog = false">Abbrechen</v-btn>
            <v-btn color="success" depressed class="rounded-lg" @click="linkCustomer" :loading="linking" :disabled="!linkEmailInput">
              Verknüpfen
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showMagicDialog" max-width="450">
      <v-card color="#121212" dark class="rounded-xl border-glow">
        <div class="pa-5">
          <div class="d-flex align-center mb-4">
            <v-icon color="purple accent-2" class="mr-2">mdi-auto-fix</v-icon>
            <span class="text-h6 font-weight-bold white--text">AI Anweisung</span>
          </div>

          <p class="body-2 grey--text text--lighten-1 mb-4">Gib Roody neue Instruktionen für den Entwurf.</p>

          <v-textarea
            v-model="aiInstructions"
            label="Prompt Instructions"
            placeholder="z.B. 'Sei etwas formeller', 'Du-Form', 'Erwähne das Sommerangebot'"
            outlined
            rounded
            rows="3"
            dark
            background-color="#1A1A1A"
            autofocus></v-textarea>

          <div class="d-flex justify-end mt-2">
            <v-btn text class="grey--text mr-2" @click="showMagicDialog = false">Abbrechen</v-btn>
            <v-btn class="gradient-btn rounded-lg" depressed @click="triggerRegenerate" :loading="isRegenerating">
              <v-icon left small>mdi-refresh</v-icon> Generieren
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import tacApi from "@/api/tacApi"
import socketService from "@/socketService"
import moment from "moment"
import userService from "@/services/userService"
import mailTerminationWizard from "./mailTerminationWizard.vue"

export default {
  name: "RoodyEmailNextGen",
  components: { mailTerminationWizard },
  data: () => ({
    userService,
    noteTypes: ["Büro", "Allgemein", "Korrespondenz", "Fitness", "Karappa"],
    activeNoteTypeIndex: 0, // Default 0 = Büro
    currentNoteText: "",
    lastUpdateInfo: "",
    savingComment: false,
    forceDraftView: false,
    searchTimeout: null,
    page: 1,
    limit: 20, // 20 Mails pro Abruf
    totalMails: 0,
    loadingMore: false, // Für den Spinner unten
    // Navigation
    drawer: false,
    folders: [{ name: "INBOX" }, { name: "Sent" }, { name: "Trash" }],
    selectedFolder: "INBOX",
    activeFolders: [],
    refreshing: false,
    showCreateFolder: false,
    newFolderName: "",

    // Linking
    showLinkDialog: false,
    linkEmailInput: "",
    linking: false,

    // Context Menu
    showContextMenu: false,
    contextMenuX: 0,
    contextMenuY: 0,
    contextMenuMail: null,

    // Data
    mails: [],
    loadingMails: false,
    activeMail: null,
    search: "",

    // UI State
    showMobileDetail: false,
    isRegenerating: false,

    // Popup Info
    showCustomerInfo: false,
    memberTab: 0,

    // Popup Magic/AI
    showMagicDialog: false,
    manualEmailInput: "",
    foundTacUser: null,
    aiInstructions: "",

    // Reply / Forward
    replyMode: false,
    isForwarding: false,
    replyText: "",
    forwardTo: "",
    sending: false,
  }),

  computed: {
    currentNoteType() {
      return this.noteTypes[this.activeNoteTypeIndex]
    },
    tacMember() {
      const data = this.activeMail?.customerData
      if (data && data.mapped) return data
      return null
    },
    isInternalUnmapped() {
      if (!this.activeMail || !this.activeMail.from) return false

      const isInternal =
        this.activeMail.from.address.toLowerCase().endsWith("@wellcomefit.ch") || this.activeMail.from.address.toLowerCase().endsWith("@wintifit.ch")
      const isMapped = this.activeMail.customerData && this.activeMail.customerData.mapped

      return isInternal && !isMapped
    },
  },

  watch: {
    "$vuetify.breakpoint.mdAndUp"(val) {
      if (val) this.showMobileDetail = false
    },
    activeMail() {
      this.forceDraftView = false
    },
    showCustomerInfo(val) {
      if (val) {
        this.loadNoteContent()
      }
    },
    // Wenn die Member-Daten neu reinkommen (z.B. nach Save) -> Refresh Text
    tacMember() {
      if (this.showCustomerInfo) {
        this.loadNoteContent()
      }
    },
    // Wenn User den Tab (Typ) wechselt -> Text austauschen
    activeNoteTypeIndex() {
      this.loadNoteContent()
    },
  },

  async created() {
    socketService.off("ai_mail:new_mail", this.handleNewMail)
    socketService.off("ai_mail:update_mail", this.handleUpdateMail)
  },

  async mounted() {
    if (this.$vuetify.breakpoint.smAndDown) {
      this.drawer = false
    }

    socketService.on("ai_mail:new_mail", this.handleNewMail)
    socketService.on("ai_mail:update_mail", this.handleUpdateMail)

    await this.fetchFolders()
    await this.loadMails("INBOX")
  },

  beforeDestroy() {
    socketService.off("ai_mail:new_mail", this.handleNewMail)
    socketService.off("ai_mail:update_mail", this.handleUpdateMail)
  },

  methods: {
    async loadEverythingAtOnce() {
      if (this.totalMails > 300) {
        const confirm = window.confirm(`Achtung: ${this.totalMails} Mails auf einmal laden kann kurz hängen. OK?`)
        if (!confirm) return
      }

      this.loadingMails = true

      // 1. Backup vom aktuellen Limit
      const originalLimit = this.limit

      // 2. Limit auf Maximum setzen
      this.limit = this.totalMails
      this.page = 1 // Wir müssen bei Seite 1 anfangen, um ALLE zu kriegen

      try {
        // 3. Laden mit reset=true (alles überschreiben)
        await this.loadMails(this.selectedFolder, true)

        this.$toast.success("Alle Mails geladen")

        // 4. Automatisch ganz nach unten scrollen
        this.$nextTick(() => {
          if (this.$refs.mailListContainer) {
            this.$refs.mailListContainer.scrollTop = this.$refs.mailListContainer.scrollHeight
          }
        })
      } finally {
        // 5. Limit wiederherstellen für normale Nutzung
        this.limit = originalLimit
        this.loadingMails = false
      }
    },
    loadNoteContent() {
      this.currentNoteText = ""
      this.lastUpdateInfo = ""

      if (!this.tacMember || !this.tacMember.comments) return

      // Suche im Array nach dem Typ (z.B. "Büro")
      const found = this.tacMember.comments.find((c) => c.type === this.currentNoteType)

      if (found) {
        this.currentNoteText = found.text
        console.log("FOUND", found)
        // Optional: Falls du das Datum im Frontend mapping hast, könnte man es anzeigen
        // this.lastUpdateInfo = moment(found.date).format("DD.MM.YYYY HH:mm")
      }
    },
    handleTerminationAndOpenNotes() {
      this.forceDraftView = false // Wizard zu
      this.$toast.success("Prozess abgeschlossen")

      // Prüfen ob wir Memberdaten haben
      if (this.tacMember) {
        this.showCustomerInfo = true // Popup öffnen
        this.memberTab = 2 // Tab auf "Notizen" wechseln (Index 2, da 0=Overview, 1=Contracts)

        // Optional: Wir könnten hier schon Text vorbefüllen
        //this.newCommentText = "Mitgliedschaft per Mail gekündigt. Bestätigung gesendet."
      }
    },
    async saveCurrentNote() {
      if (!this.tacMember || !this.tacMember.tacId) {
        this.$toast.error("Fehler: Keine TAC ID")
        return
      }

      this.savingComment = true
      try {
        let status = await tacApi.createTACUserProfileComment({
          tacId: this.tacMember.tacId,
          text: this.currentNoteText,
          type: this.currentNoteType,
        })

        this.$toast.success(`${this.currentNoteType} gespeichert`)

        if (this.activeMail && this.activeMail.customerData) {
          this.linkEmailInput = this.activeMail.customerData.email
          await this.linkCustomer(false) // false = Kein neuer Draft, nur Refresh
        }
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Speichern")
      } finally {
        this.savingComment = false
      }
    },

    onSearchInput(val) {
      // Debounce: Warte 600ms nachdem der User aufgehört hat zu tippen
      if (this.searchTimeout) clearTimeout(this.searchTimeout)

      this.searchTimeout = setTimeout(() => {
        this.page = 1 // Reset Page bei neuer Suche
        this.loadMails(this.selectedFolder, true)
      }, 600)
    },
    // --- SOCKET HANDLER METHODS ---
    onScroll(e) {
      const { scrollTop, clientHeight, scrollHeight } = e.target

      // Wenn wir fast unten sind (ca 50px Puffer) und nicht gerade laden
      if (scrollTop + clientHeight >= scrollHeight - 50) {
        // Prüfen ob es noch mehr Mails gibt
        if (!this.loadingMails && !this.loadingMore && this.mails.length < this.totalMails) {
          this.loadNextPage()
        }
      }
    },
    // Neue Helper Methode für Page > 1
    async loadNextPage() {
      this.loadingMore = true
      this.page++
      await this.loadMails(this.selectedFolder, false) // false = nicht resetten
      this.loadingMore = false
    },
    handleNewMail(newMail) {
      // Live Mode: Wir verlassen uns auf Refresh, außer bei eigenen Drafts
    },

    handleUpdateMail(updatedMail) {
      // Find by ID (UID or MongoID) or MessageID
      const index = this.mails.findIndex((m) => m._id === updatedMail._id || m.messageId === updatedMail.messageId || m.uid === updatedMail.uid)

      if (index !== -1) {
        const current = this.mails[index]

        // Prepare merged object
        // 1. Keep current structure (has 'uid', 'folder', etc.)
        // 2. Overwrite with DB data (has 'draft', 'category', 'customerData')
        const merged = { ...current, ...updatedMail }

        // 3. IMPORTANT: Map the DB ID to mongoId, and keep UI ID as UID
        if (updatedMail._id) {
          merged.mongoId = updatedMail._id
        }

        if (current.uid) {
          merged.uid = current.uid
          merged._id = current.uid // Keep UID as list key
        }

        this.$set(this.mails, index, merged)

        if (this.activeMail && this.activeMail.messageId === updatedMail.messageId) {
          const body = this.activeMail.body
          const textBody = this.activeMail.textBody
          const att = this.activeMail.attachments

          this.activeMail = {
            ...merged,
            body: body || merged.body,
            textBody: textBody || merged.textBody,
            attachments: att || merged.attachments,
          }

          if (this.isRegenerating) {
            this.isRegenerating = false
            this.showMagicDialog = false
            this.$toast.success("AI Update erhalten")
          }
        }
      } else {
        // Wenn Mail nicht gefunden (z.B. neu reingekommen) und wir im INBOX sind
        if (updatedMail.folder === this.selectedFolder) {
          // Neue Mail oben einfügen
          this.mails.unshift(updatedMail)
          this.totalMails++
        }
      }
    },

    // --- METHODS ---

    async refreshAll() {
      this.refreshing = true
      await this.fetchFolders()
      await this.loadMails(this.selectedFolder)
      this.refreshing = false
      this.$toast.success("Aktualisiert")
    },

    async createFolder() {
      try {
        await api.mail.createFolder(this.newFolderName)
        this.showCreateFolder = false
        this.newFolderName = ""
        this.$toast.success("Ordner erstellt")
        this.fetchFolders()
      } catch (e) {
        this.$toast.error("Fehler beim Erstellen")
      }
    },

    openLinkDialog() {
      this.linkEmailInput = ""

      if (this.activeMail) {
        // Wir suchen lieber im textBody, falls vorhanden, sonst im HTML body
        const content = this.activeMail.textBody || this.activeMail.body || ""

        // Einfacher, aber robuster Email-Regex
        const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/gi
        const matches = content.match(emailRegex)

        if (matches) {
          // Wir loopen durch alle gefundenen Mails
          for (const email of matches) {
            const lowerEmail = email.toLowerCase()

            // Check: Ist es KEINE interne Adresse?
            if (!lowerEmail.endsWith("@wellcomefit.ch") && !lowerEmail.endsWith("@wintifit.ch")) {
              this.linkEmailInput = lowerEmail // Treffer!
              break // Loop beenden, wir haben die erste gefunden
            }
          }
        }
      }

      this.showLinkDialog = true
    },

    async linkCustomer(createDraft = true) {
      if (!this.linkEmailInput || !this.activeMail) return
      this.linking = true
      try {
        const updated = await api.mail.linkCustomer({
          messageId: this.activeMail.messageId,
          uid: this.activeMail.uid,
          from: this.activeMail.from,
          subject: this.activeMail.subject,
          date: this.activeMail.date,
          folder: this.activeMail.folder,
          manualEmail: this.linkEmailInput,
        })

        // Local Update
        this.handleUpdateMail(updated)

        this.$toast.success("Verknüpft")
        if (createDraft) this.createDraft()
        this.showLinkDialog = false
      } catch (e) {
        this.$toast.error("Konnte nicht verknüpft werden (evtl. falsche Email)")
      } finally {
        this.linking = false
      }
    },

    async assignUser(action) {
      if (!this.activeMail) return
      try {
        const updated = await api.mail.assignUser({
          mailId: this.activeMail.mongoId || this.activeMail._id,
          messageId: this.activeMail.messageId,
          uid: this.activeMail.uid,
          from: this.activeMail.from,
          subject: this.activeMail.subject,
          date: this.activeMail.date,
          folder: this.activeMail.folder,
          action: action,
        })

        this.handleUpdateMail(updated)
        this.$toast.success(action === "assign" ? "Übernommen" : "Freigegeben")
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },

    async downloadAttachment(att) {
      try {
        const response = await api.mail.downloadAttachment(this.activeMail.folder, this.activeMail.uid, att.checksum, att.filename)

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement("a")
        link.href = url
        link.setAttribute("download", att.filename)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error(e)
        this.$toast.error("Download fehlgeschlagen")
      }
    },

    async createDraft() {
      if (!this.activeMail) return

      this.activeMail.category = "Processing..." // UI Feedback
      console.log(this.activeMail)
      try {
        await api.mail.generateDraft({
          messageId: this.activeMail.messageId,
          uid: this.activeMail.uid, // Fallback ID
          from: this.activeMail.from,
          subject: this.activeMail.subject,
          textBody: this.activeMail.textBody || this.getPlainText(this.activeMail.body) || "",
          folder: this.activeMail.folder,
        })
        this.$toast.info("AI analysiert...")
      } catch (e) {
        this.$toast.error("Fehler beim Starten der Analyse")
        this.activeMail.category = "Inbox"
      }
    },
    getPlainText(htmlContent) {
      if (!htmlContent) return ""
      // Erstelle ein temporäres Element
      const tempDiv = document.createElement("div")
      tempDiv.innerHTML = htmlContent
      // Hol dir nur den Text-Inhalt (textContent ist performanter als innerText)
      return tempDiv.textContent || ""
    },

    formatDate(date) {
      const m = moment(date)
      if (m.isSame(moment(), "day")) return m.format("HH:mm")
      return m.format("DD.MM.")
    },

    formatFolderName(folder) {
      return folder.replace("INBOX.", "")
    },

    getFolderIcon(folderName) {
      const n = folderName.toLowerCase()
      if (n.includes("inbox")) return "mdi-inbox-arrow-down"
      if (n.includes("sent") || n.includes("gesendet")) return "mdi-send-outline"
      if (n.includes("trash") || n.includes("papierkorb") || n.includes("deleted")) return "mdi-delete-outline"
      if (n.includes("junk") || n.includes("spam")) return "mdi-alert-octagon-outline"
      if (n.includes("archive") || n.includes("archiv")) return "mdi-archive-outline"
      if (n.includes("draft") || n.includes("entwurf")) return "mdi-file-document-edit-outline"
      return "mdi-folder-outline"
    },

    getCategoryColor(cat) {
      if (!cat) return "grey darken-3"
      const c = cat.toLowerCase()
      if (c.includes("cancel") || c.includes("künd")) return "red darken-2"
      if (c.includes("sales") || c.includes("verkau")) return "green darken-2"
      if (c.includes("support")) return "blue darken-2"
      return "grey darken-2"
    },

    onDragStart(evt, mail) {
      evt.dataTransfer.setData("mailId", mail._id) // UID
    },

    async onDrop(evt, targetFolder) {
      const uid = evt.dataTransfer.getData("mailId") // UID
      if (!uid) return

      // Optimistic update
      const mailIndex = this.mails.findIndex((m) => m._id == uid)
      if (mailIndex > -1) this.mails.splice(mailIndex, 1)

      try {
        // We assume targetFolder is path
        // We assume current folder is this.selectedFolder
        await api.mail.moveMails([uid], this.selectedFolder, targetFolder)
        this.$toast.success(`Verschoben nach ${targetFolder}`)
      } catch (e) {
        this.$toast.error("Fehler beim Verschieben")
        await this.loadMails(this.selectedFolder)
      }
    },

    async fetchFolders() {
      try {
        const res = await api.mail.getFolders()
        if (Array.isArray(res) && res.length > 0) {
          this.folders = res
          const currentExists = this.folders.find((f) => f.path === this.selectedFolder)
          if (!currentExists) {
            const inbox = this.folders.find((f) => f.name.toUpperCase() === "INBOX")
            this.selectedFolder = inbox ? inbox.path : this.folders[0].path
          }
        }
      } catch (e) {
        console.error("Konnte Ordner nicht laden:", e)
        this.folders = [{ name: "INBOX", path: "INBOX" }]
      }
    },

    onFolderSelect(active) {
      if (active.length > 0) {
        // Avoid infinite loop if already selected
        if (active[0] !== this.selectedFolder) {
          this.loadMails(active[0])
        }
      }
    },

    async loadMails(folder, reset = true) {
      // Reset Logik
      if (reset) {
        this.loadingMails = true
        this.page = 1
        this.mails = [] // Liste leeren
        this.totalMails = 0

        // Scroll nach oben setzen falls Container ref existiert
        if (this.$refs.mailListContainer) {
          this.$refs.mailListContainer.scrollTop = 0
        }
      }

      this.selectedFolder = folder

      // Update active folders für Treeview
      if (!this.activeFolders.includes(folder)) {
        this.activeFolders = [folder]
      }

      // Mobile Handling
      if (this.$vuetify.breakpoint.smAndDown && reset) {
        this.activeMail = null
        this.showMobileDetail = false
      }
      if (reset) this.replyMode = false

      try {
        // API Aufruf mit Pagination Params
        const res = await api.mail.getMails({
          folder,
          page: this.page,
          limit: this.limit,
          search: this.search,
        })

        // Das Backend liefert { mails: [], total: 123 }

        const newMails = res.mails || [] // Axios wrapper check
        this.totalMails = res.total || 0

        if (reset) {
          this.mails = newMails
        } else {
          // Anfügen
          this.mails = [...this.mails, ...newMails]
        }
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Laden der Mails")
        // Falls Fehler bei Seite 2+, Page count zurücksetzen
        if (!reset && this.page > 1) this.page--
      } finally {
        this.loadingMails = false
      }
    },

    async selectMail(mail) {
      if (mail.category === "Processing...") return

      this.activeMail = mail
      this.replyMode = false
      this.showCustomerInfo = false

      if (this.$vuetify.breakpoint.smAndDown) {
        this.showMobileDetail = true
      }

      // Check Content
      if (!mail.body && !mail.loadingBody) {
        this.$set(mail, "loadingBody", true)
        try {
          // Use UID if available (which we set as _id in backend)
          const uid = mail.uid || mail._id
          const fullData = await api.mail.getMailContent(mail.folder, uid)

          // Merge Data
          Object.assign(mail, fullData)
          mail.loadingBody = false

          // Force update in list
          const idx = this.mails.findIndex((m) => m._id === mail._id)
          if (idx !== -1) this.$set(this.mails, idx, mail)
        } catch (e) {
          console.error(e)
          this.$toast.error("Inhalt konnte nicht geladen werden")
          mail.loadingBody = false
        }
      }

      // Mark Read locally (Backend did it on fetch)
      if (!mail.isRead) {
        mail.isRead = true
        // api.mail.updateFlags(...) // Done by fetch automatically
      }
    },

    showContext(e, mail) {
      e.preventDefault()
      this.showContextMenu = false
      this.contextMenuX = e.clientX
      this.contextMenuY = e.clientY
      this.contextMenuMail = mail
      this.$nextTick(() => {
        this.showContextMenu = true
      })
    },

    async toggleReadStatus(mail) {
      const newStatus = !mail.isRead
      // Optimistic update
      mail.isRead = newStatus

      try {
        await api.mail.updateFlags(mail.folder, mail.uid, mail.messageId, newStatus)
      } catch (e) {
        mail.isRead = !newStatus // revert
        this.$toast.error("Fehler beim Aktualisieren")
      }
    },

    async archiveMail(mail) {
      this.onDrop({ dataTransfer: { getData: () => mail._id } }, "Archive")
    },

    async deleteMail(mail) {
      this.onDrop({ dataTransfer: { getData: () => mail._id } }, "Trash")
    },

    async lookupTacUser() {
      if (!this.manualEmailInput) return
      try {
        const res = await api.mail.manualTacLookup(this.manualEmailInput)
        if (res && res.mapped) {
          this.foundTacUser = res
          this.$toast.success("Mitglied gefunden!")
        } else {
          this.$toast.info("Nichts gefunden")
          this.foundTacUser = null
        }
      } catch (e) {
        this.$toast.error("Fehler")
      }
    },

    async triggerRegenerate() {
      if (!this.activeMail) return
      this.isRegenerating = true
      this.showMagicDialog = false

      try {
        // NEW: pass folder & uid
        await api.mail.regenerate({
          mailId: this.activeMail._id, // This might be UID or MongoID depending on object source.
          // Actually, backend expects MongoID for regenerate route if using DB findById,
          // BUT we switched DB to be metadata only.
          // Wait, the backend regenerate tries to find by ID.
          // If the mail has a MongoID (because we created a draft), we use that.
          // If it's pure IMAP, we don't have a Mongo ID yet.
          // But generateDraft creates one!
          // So regeneration implies we already have a draft. Correct.
          mailId: this.activeMail.mongoId || this.activeMail._id, // Prefer MongoID if available
          folder: this.activeMail.folder,
          uid: this.activeMail.uid || this.activeMail._id,

          newCustomerEmail: this.foundTacUser ? this.manualEmailInput : null,
          instructions: this.aiInstructions,
        })

        this.foundTacUser = null
        this.aiInstructions = ""
        this.manualEmailInput = ""
        this.$toast.info("AI arbeitet...")
      } catch (e) {
        this.isRegenerating = false
        this.$toast.error("Fehler")
      }
    },

    initReply() {
      this.replyMode = true
      this.isForwarding = false
      if (this.activeMail.draft) {
        this.replyText = this.activeMail.draft
      } else {
        this.replyText = `Hallo ${this.activeMail.from.name},\n\n`
      }
    },

    openForward() {
      this.replyMode = true
      this.isForwarding = true
      this.forwardTo = ""
      this.replyText = `
---------- Forwarded message ---------
From: ${this.activeMail.from.name} <${this.activeMail.from.address}>
Date: ${new Date(this.activeMail.date).toLocaleString()}
Subject: ${this.activeMail.subject}

${this.activeMail.textBody || "Inhalt nicht verfügbar"}
`
    },

    async sendReply() {
      this.sending = true
      try {
        const payload = {
          html: this.replyText.replace(/\n/g, "<br>"),
          replyToId: this.activeMail.mongoId, // Use MongoID if available for threading
        }
        if (this.isForwarding) {
          payload.to = this.forwardTo
          payload.subject = "Fwd: " + this.activeMail.subject
        } else {
          payload.to = this.activeMail.from.address
          payload.subject = "Re: " + this.activeMail.subject
        }
        await api.mail.send(payload)
        this.$toast.success("Gesendet")
        this.replyMode = false
      } catch (e) {
        this.$toast.error("Fehler beim Senden")
      } finally {
        this.sending = false
      }
    },
  },
}
</script>

<style scoped>
/* =========================================

ROODY NEXT GEN STYLES (Deep Dark)

========================================= */

@import url("https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap");

.font-sans {
  font-family: "Inter", sans-serif !important;
}

.font-mono {
  font-family: "JetBrains Mono", monospace;
}

.tracking-tight {
  letter-spacing: -0.02em;
}

.tracking-wide {
  letter-spacing: 0.05em;
}

/* Backgrounds */

.app-bg {
  background-color: #050505;
}

.bg-pattern {
  background-color: #050505;

  background-image: radial-gradient(circle at 1px 1px, rgba(255, 255, 255, 0.05) 1px, transparent 0);

  background-size: 40px 40px;
}

.bg-gradient-subtle {
  background: linear-gradient(180deg, rgba(18, 18, 18, 1) 0%, rgba(5, 5, 5, 1) 100%);
}

/* Glassmorphism */

.glass-header {
  background: rgba(18, 18, 18, 0.75);

  backdrop-filter: blur(16px);

  -webkit-backdrop-filter: blur(16px);

  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
}

.glass-panel {
  background: rgba(255, 255, 255, 0.03);

  backdrop-filter: blur(10px);

  border: 1px solid rgba(255, 255, 255, 0.05);
}

.glass-panel-dark {
  background: rgba(10, 10, 10, 0.8);

  backdrop-filter: blur(20px);

  border: 1px solid rgba(255, 255, 255, 0.08);
}

.glass-pill {
  background: rgba(255, 255, 255, 0.05);

  border-radius: 50px;

  border: 1px solid rgba(255, 255, 255, 0.05);
}

/* Layout Columns */

.sidebar-panel {
  width: 260px;

  background-color: #000000;

  flex-shrink: 0;

  border-right: 1px solid rgba(255, 255, 255, 0.04);
}

.list-panel {
  width: 380px;

  background-color: #0a0a0a;

  flex-shrink: 0;
}

@media (max-width: 959px) {
  .list-panel {
    width: 100%;
  }
}

.detail-panel {
  background-color: #050505;

  position: relative;

  overflow: hidden;
}

.mobile-fullscreen {
  position: absolute;

  top: 0;

  left: 0;

  width: 100%;

  height: 100%;

  z-index: 50;

  background-color: #050505;
}

/* Gradients & Glows */

.gradient-btn {
  background: linear-gradient(135deg, #7c4dff 0%, #448aff 100%);

  box-shadow: 0 4px 15px rgba(124, 77, 255, 0.3);

  transition: all 0.3s ease;
}

.gradient-btn:hover {
  box-shadow: 0 6px 20px rgba(124, 77, 255, 0.5);

  transform: translateY(-1px);
}

.gradient-header {
  background: linear-gradient(to right bottom, #212121, #000000);
}

.brand-glow {
  width: 32px;

  height: 32px;

  background: linear-gradient(135deg, #7c4dff, #00e5ff);

  box-shadow: 0 0 15px rgba(124, 77, 255, 0.4);
}

.active-folder-desktop {
  background: rgba(255, 255, 255, 0.06);

  border-right: 3px solid #7c4dff;
}

/* Mail Cards */

.mail-card {
  background: rgba(255, 255, 255, 0.02);

  border: 1px solid transparent;

  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.mail-card:hover {
  background: rgba(255, 255, 255, 0.05);

  transform: translateY(-2px);

  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
}

.active-mail-card {
  background: rgba(124, 77, 255, 0.08) !important;

  border: 1px solid rgba(124, 77, 255, 0.3) !important;

  box-shadow: 0 0 20px rgba(124, 77, 255, 0.05);
}

.unread-mail {
  border-left: 3px solid #00e5ff;
}

/* Mail Render */

::v-deep .mail-renderer * {
  -webkit-user-select: text !important;
  -ms-user-select: text !important;
  user-select: text !important;
}

.mail-renderer {
  background-color: #ffffff;

  color: #111;

  /* Smart Invert for dark mode logic */

  filter: invert(0.92) hue-rotate(180deg);
}

.mail-renderer.strip-images >>> img {
  display: none !important;
}

.mail-renderer >>> a {
  color: #2962ff;

  font-weight: bold;
}

/* AI Elements */

.ai-analysis-box {
  background: linear-gradient(145deg, rgba(124, 77, 255, 0.05) 0%, rgba(0, 0, 0, 0) 100%);

  border: 1px solid rgba(124, 77, 255, 0.2);
}

.ai-draft-card {
  border: 1px solid rgba(124, 77, 255, 0.2);

  background: #0f0f0f;
}

.draft-header {
  background: linear-gradient(90deg, #7c4dff 0%, #448aff 100%);
}

.ai-loader {
  width: 40px;

  height: 40px;

  border: 3px solid rgba(124, 77, 255, 0.3);

  border-radius: 50%;

  border-top-color: #7c4dff;

  animation: spin 1s ease-in-out infinite;
}

/* Inputs */

.modern-search-field >>> .v-input__slot {
  min-height: 40px !important;
}

.modern-input {
  background: transparent;

  border: none;

  color: white;

  outline: none;

  font-size: 14px;
}

.no-border-input >>> .v-input__slot::before,
.no-border-input >>> .v-input__slot::after {
  display: none !important;
}

/* Scrollbars */

.custom-scrollbar::-webkit-scrollbar {
  width: 6px;
}

.custom-scrollbar::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.1);

  border-radius: 4px;
}

.custom-scrollbar::-webkit-scrollbar-track {
  background: transparent;
}

/* Utilities */

.border-subtle {
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.border-b-subtle {
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.border-t-subtle {
  border-top: 1px solid rgba(255, 255, 255, 0.05);
}

.border-r-subtle {
  border-right: 1px solid rgba(255, 255, 255, 0.05);
}

.border-glow {
  border: 1px solid rgba(255, 255, 255, 0.1);

  box-shadow: 0 0 30px rgba(0, 0, 0, 0.5);
}

.z-index-10 {
  z-index: 10;

  position: sticky;

  top: 0;
}

.pointer {
  cursor: pointer;
}

.shadow-lg {
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

/* Animations */

.fade-in-out {
  animation: fade 2s infinite;
}

@keyframes fade {
  0%,
  100% {
    opacity: 0.5;
  }

  50% {
    opacity: 1;
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
